import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  LocationType,
  InitialStateType,
  SetCatalogueImagePayloadActionType,
  SetImageRulesPayloadActionType,
  SetFormsPayloadActionType,
  SetImageBannerRulesPayloadActionType,
  SetFormBannerPayloadActionType,
  CatalogueImageType,
  CatalogueBackgroundTypeEnum,
  SetBackgroundFormPayloadActionType,
  CatalogueBackgroundFormType,
  CatalogBackgroundType,
  FormBannerStateType,
  NewImageType,
  ImageType,
  ImageRulesType,
} from 'features/CategoryAndPosisi/@types/catalogue'
import { fetchSegmentList } from 'features/BannerAndTicker/redux/banner'
import { SLICE_NAME, fetchCatalogueImageTypes } from './thunk'

export { fetchSegmentList }

const NEW_IMAGE_FORMAT: NewImageType = {
  newImage: null,
  newImagePreviewUrl: '',
  newImageUrl: '',
  newImageUrlLarge: '',
  newImageUrlMedium: '',
  newImageUrlSmall: '',
}
export const IMAGE_FORMAT: ImageType = {
  image: null,
  imagePreviewUrl: '',
  imageUrl: '',
  imageUrlLarge: '',
  imageUrlMedium: '',
  imageUrlSmall: '',
}

const generateEmptyImageRules = (typeId: ImageRulesType['typeId']): ImageRulesType => ({
  extensions: [],
  height: { max: 0, min: 0 },
  maxFileSize: 0,
  typeId,
  width: { max: 0, min: 0 },
})

export const initialState: InitialStateType = {
  isLoading: false,
  selectedHub: [],
  catalogueImage: {
    big: NEW_IMAGE_FORMAT,
    icon: IMAGE_FORMAT,
    regular: {
      ...NEW_IMAGE_FORMAT,
      oldImage: null,
      oldImagePreviewUrl: '',
      oldImageUrl: '',
      oldImageUrlLarge: '',
      oldImageUrlMedium: '',
      oldImageUrlSmall: '',
    },
    vertical: NEW_IMAGE_FORMAT,
    subCatalogue: IMAGE_FORMAT,
    brand: NEW_IMAGE_FORMAT,
  },
  imageRules: generateEmptyImageRules('homepage-catalog'),
  imageBigRules: generateEmptyImageRules('homepage-catalog-big'),
  imageVerticalRules: generateEmptyImageRules('homepage-catalog-vertical'),
  imageBannerRules: {
    newBanner: generateEmptyImageRules('homepage-banner'),
    oldBanner: generateEmptyImageRules('banner-images'),
  },
  imageBrandRules: generateEmptyImageRules('homepage-catalog-brand'),
  forms: { categoryLabel: '', segment: [], imageTabbingFile: null, imageTabbing: null },
  formBanner: {
    bannerCategory: null,
    bannerImage: '',
    bannerLink: '',
    bannerTitle: '',
    bannerFile: null,
    destinationBanner: 'url',
    endDate: '',
    endTime: '',
    startDate: '',
    startTime: '',
  },
  catalogueTypes: [],
  selectedCatalogType: null,
  imageBackgroundRules: generateEmptyImageRules('homepage-catalog-background'),
  background: {
    isBackgroundDialogOpen: false,
    form: {
      title: '',
      bg_image_file: null,
      bg_image_preview: '',
      bg_type: CatalogueBackgroundTypeEnum.PLAIN,
    },
    detail: {},
  },
  segmentOptions: [],
  imageTabbingRules: null,
  imageURLToPreview: '',
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetState: () => initialState,
    resetCatalogueImage: (state) => {
      state.catalogueImage = initialState.catalogueImage
    },
    resetForms: (state) => {
      state.forms = initialState.forms
    },
    resetFormBanner: (state, { payload }: PayloadAction<FormBannerStateType | undefined>) => {
      state.formBanner = payload ? { ...payload } : initialState.formBanner
    },
    setSelectedHub: (state, { payload }: PayloadAction<LocationType[]>) => {
      state.selectedHub = [...payload]
    },
    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload
    },
    setImageRules: (state, action: PayloadAction<SetImageRulesPayloadActionType>) => {
      const { value } = action.payload

      state.imageRules = value
    },
    setImageBigRules: (state, action: PayloadAction<SetImageRulesPayloadActionType>) => {
      const { value } = action.payload

      state.imageBigRules = value
    },
    setImageVerticalRules: (state, action: PayloadAction<SetImageRulesPayloadActionType>) => {
      const { value } = action.payload

      state.imageVerticalRules = value
    },
    setImageBannerRules: (state, action: PayloadAction<SetImageBannerRulesPayloadActionType>) => {
      const { value, name } = action.payload
      state.imageBannerRules[name] = value
    },
    setCatalogueImage: (
      state,
      { payload: { name, value } }: PayloadAction<SetCatalogueImagePayloadActionType>,
    ) => {
      state.catalogueImage = { ...state.catalogueImage, [name]: value }
    },
    setForms: (state, { payload: { name, value } }: PayloadAction<SetFormsPayloadActionType>) => {
      state.forms[name] = value as never
    },
    setFormBanner: (
      state,
      { payload: { name, value } }: PayloadAction<SetFormBannerPayloadActionType>,
    ) => {
      state.formBanner[name] = value as never
    },
    setSelectedCatalogueType: (state, { payload }: PayloadAction<Nullable<CatalogueImageType>>) => {
      state.selectedCatalogType = payload
    },

    setBackgroundDialog: (state, { payload }: PayloadAction<boolean>) => {
      state.background.isBackgroundDialogOpen = payload
    },
    setBackgroundForm: (state, { payload }: PayloadAction<SetBackgroundFormPayloadActionType>) => {
      const { key, value } = payload

      if (key) {
        state.background.form[key] = value as never
      } else {
        state.background.form = { ...(value as CatalogueBackgroundFormType) }
      }
    },
    setImageBackgroundRules: (state, action: PayloadAction<SetImageRulesPayloadActionType>) => {
      const { value } = action.payload

      state.imageBackgroundRules = value
    },
    setImageBrandRules: (state, action: PayloadAction<SetImageRulesPayloadActionType>) => {
      const { value } = action.payload

      state.imageBrandRules = value
    },
    setBackgroundDetail: (
      state,
      { payload }: PayloadAction<CatalogBackgroundType | ObjectNullType>,
    ) => {
      state.background.detail = { ...payload }
    },
    setImageTabbingRules: (
      state,
      { payload }: PayloadAction<InitialStateType['imageTabbingRules']>,
    ) => {
      state.imageTabbingRules = payload
    },
    setImageURLToPreview: (state, { payload }: PayloadAction<string>) => {
      state.imageURLToPreview = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalogueImageTypes.fulfilled, (state, { payload }) => {
        state.catalogueTypes = [...payload]
      })
      .addCase(fetchSegmentList.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchSegmentList.rejected, (state) => {
        state.isLoading = true
      })
      .addCase(fetchSegmentList.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.segmentOptions = payload
      })
  },
})

export const {
  resetState,
  setCatalogueImage,
  setSelectedHub,
  setImageRules,
  resetCatalogueImage,
  setImageBigRules,
  setForms,
  resetForms,
  setImageBannerRules,
  setFormBanner,
  resetFormBanner,
  setIsLoading,
  setSelectedCatalogueType,
  setImageVerticalRules,
  setBackgroundForm,
  setBackgroundDialog,
  setImageBackgroundRules,
  setBackgroundDetail,
  setImageTabbingRules,
  setImageURLToPreview,
  setImageBrandRules,
} = slice.actions
export default slice.reducer
